import { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AboutPage = () => {
  const philosophyAnimation = useScrollAnimation();
  const companyAnimation = useScrollAnimation();
  const mapAnimation = useScrollAnimation();
  const historyAnimation = useScrollAnimation();

  useEffect(() => {
    const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
    
    document.title = '岡山市の英会話教室の会社概要｜おかやま英会話教室株式会社';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市北区の英会話教室、おかやま英会話教室株式会社の会社概要。2023年設立、地域密着型の英語教育で子供から大人まで幅広くサポート。アクセス情報も掲載。');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '岡山英会話教室会社概要,岡山市北区英語スクール,会社情報,アクセス,企業理念');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/about`);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/about`);
    }
    
    // Add Schema.org JSON-LD for About page
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-page-schema]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "EducationalOrganization",
        "name": "おかやま英会話教室株式会社",
        "url": `${siteUrl}/`,
        "logo": `${siteUrl}/logo.png`,
        "description": "岡山市北区の英会話教室。2023年設立、地域密着型の英語教育で子供から大人まで幅広くサポート。",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "○○町1-2-3",
          "addressLocality": "岡山市北区",
          "addressRegion": "岡山県",
          "postalCode": "700-0000",
          "addressCountry": "JP"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "34.6617",
          "longitude": "133.9350"
        },
        "telephone": "+81-86-012-3456",
        "faxNumber": "+81-86-012-3457",
        "email": "info@okayama-english.jp",
        "founder": {
          "@type": "Person",
          "name": "岡山太郎",
          "jobTitle": "代表取締役"
        },
        "foundingDate": "2023",
        "numberOfEmployees": 9,
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', 'true');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
  }, []);

  const historyItems = [
    { year: '2023年4月', event: 'おかやま英会話教室株式会社設立' },
    { year: '2023年6月', event: '岡山市北区に本校開校、キッズ・大人コース開講' },
    { year: '2023年10月', event: 'オンラインレッスンコース開始' },
    { year: '2024年3月', event: '受講生徒数300名突破' },
    { year: '2025年1月', event: '受講生徒数500名突破' }
  ];

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center mt-20">
        <img
          src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8691fc9fef1272e5a23b582a9f01cf22.jpeg"
          alt="会社概要"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        
        <div className="relative z-10 text-center text-navy-900 max-w-4xl mx-auto px-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            About
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed">
            地域に根ざした英語教育を目指して
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 bg-white">
        <div 
          ref={philosophyAnimation.ref}
          className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${philosophyAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-12">
            <span className="bg-mint-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              OUR PHILOSOPHY
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mt-6 mb-8">
              私たちの想い
            </h2>
          </div>
          
          <div className="space-y-8 text-center mb-12">
            <p className="text-gray-700 text-lg leading-relaxed">
              この度は、おかやま英会話教室のホームページをご覧いただき、誠にありがとうございます。
              代表の岡山太郎と申します。
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              私は長年、英語教育に携わる中で、「英語を学ぶ楽しさ」と「世界とつながる喜び」を
              一人でも多くの方に伝えたいという想いを抱いてまいりました。
              その想いを実現するため、2023年にこの教室を設立いたしました。
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              当教室では、子どもから大人まで、それぞれの目標やレベルに合わせた
              きめ細やかな指導を心がけております。英語は単なる学習科目ではなく、
              新しい世界への扉を開く鍵であり、人生を豊かにする大切なツールです。
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              地域の皆様に愛され、信頼される教室として、これからも質の高い教育サービスを
              提供してまいります。皆様のご来校を心よりお待ちしております。
            </p>
            <p className="text-gray-700 text-lg leading-relaxed font-medium">
              おかやま英会話教室株式会社<br />
              代表取締役 岡山 太郎
            </p>
          </div>
          
          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://public.readdy.ai/ai/img_res/aaffd010c1622f96f2f8c5be2b9a1bab.jpg"
              alt="スタッフ集合写真"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-20 bg-beige-50">
        <div 
          ref={companyAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${companyAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="bg-navy-900 text-white px-4 py-2 rounded-full text-sm font-medium">
                COMPANY INFO
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-8">
                会社情報
              </h2>
              <div className="w-full h-80 rounded-2xl overflow-hidden shadow-lg mb-8">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/7bfe5a15aa32e4fb2b4281eaa548dbee.jpeg"
                  alt="おかやま英会話教室の外観"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <dl className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">会社名</dt>
                  <dd className="text-gray-700">おかやま英会話教室株式会社</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">代表取締役</dt>
                  <dd className="text-gray-700">岡山 太郎</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">所在地</dt>
                  <dd className="text-gray-700">〒700-0000 岡山県岡山市北区○○町1-2-3</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">電話番号</dt>
                  <dd className="text-gray-700">086-012-3456</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">FAX</dt>
                  <dd className="text-gray-700">086-012-3457</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">設立</dt>
                  <dd className="text-gray-700">2023年</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">従業員数</dt>
                  <dd className="text-gray-700">9名</dd>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <dt className="font-bold text-navy-900 mb-2">営業時間</dt>
                  <dd className="text-gray-700">9:00 〜 18:00</dd>
                </div>
                <div>
                  <dt className="font-bold text-navy-900 mb-2">定休日</dt>
                  <dd className="text-gray-700">日曜日・祝日</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Access Map Section */}
      <section className="py-20 bg-white">
        <div 
          ref={mapAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${mapAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-12">
            <span className="bg-coral-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              ACCESS
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-4">
              アクセス
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl mb-8">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26234.27771405956!2d133.91503!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e59fffff%3A0x301a965e4db1d!2z5bKh5bGx55yM5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="おかやま英会話教室の地図"
            ></iframe>
          </div>
          
          <div className="text-center">
            <p className="text-gray-700 text-lg mb-2">
              <i className="ri-map-pin-line text-coral-500 mr-2"></i>
              〒700-0000 岡山県岡山市北区○○町1-2-3
            </p>
            <p className="text-gray-600">
              JR岡山駅より徒歩10分 / 専用駐車場10台完備
            </p>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 bg-beige-50">
        <div 
          ref={historyAnimation.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${historyAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-12">
            <span className="bg-mint-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              HISTORY
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-4">
              沿革
            </h2>
          </div>
          
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-mint-500"></div>
            
            <div className="space-y-8">
              {historyItems.map((item, index) => (
                <div key={index} className="relative pl-20">
                  <div className="absolute left-5 top-2 w-6 h-6 bg-mint-500 rounded-full border-4 border-white shadow"></div>
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <div className="font-bold text-coral-500 text-lg mb-2">
                      {item.year}
                    </div>
                    <p className="text-gray-700 text-base">
                      {item.event}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AboutPage;